import { useLingui } from "@lingui/react/macro";
import { Button, cn } from "@rakazo/ui-web";
import { AudioLines, Mic, MicOff, PhoneOff, RotateCcw, XIcon } from "lucide-react";
import type { LiveVoiceSnapshot } from "../../lib/live-voice";
import type { LiveHistoryStatus } from "../../lib/live-voice-recording";

const WAVE_HEIGHTS = [0.3, 0.6, 0.9, 0.5, 1, 0.7, 0.4, 0.8, 0.5];

/** Compact call controls keep the chat available; transcript stays in session state.
 * Компактна панель лишає чат доступним; розшифровка зберігається у стані розмови. */
export function LiveVoiceOverlay({
  snapshot,
  botName,
  onEnd,
  onRetry,
  onToggleMute,
  onBackToText,
  historyStatus,
  onRetryHistory,
}: {
  snapshot: LiveVoiceSnapshot;
  botName?: string;
  onEnd: () => void;
  onRetry?: () => void;
  onToggleMute: () => void;
  onBackToText: () => void;
  historyStatus?: LiveHistoryStatus;
  onRetryHistory?: () => void;
}) {
  const { t } = useLingui();
  const failed = snapshot.phase === "error";
  const ended = snapshot.phase === "ended";
  const connecting = snapshot.phase === "connecting";
  const reconnecting = snapshot.phase === "reconnecting";
  const status = failed
    ? t`Stopped`
    : ended
      ? t`Ended`
      : connecting
        ? t`Connecting…`
        : reconnecting
          ? t`Reconnecting…`
          : snapshot.muted
            ? t`Muted`
            : t`Listening`;
  const level =
    failed || ended
      ? 0
      : Math.max(snapshot.muted ? 0 : snapshot.level, snapshot.outputText ? 0.55 : 0);
  const error =
    snapshot.error === "Live audio needs a tap to resume."
      ? t`Tap to resume audio. Your tasks are still running.`
      : snapshot.error === "Live connection is too slow. Try again."
        ? t`Connection is too slow. Try again.`
        : snapshot.error === "Live connection timed out. Try again."
          ? t`Connection timed out. Try again.`
          : snapshot.error === "Live audio could not keep up. Try again."
            ? t`Audio playback could not keep up. Try again.`
            : snapshot.error;

  return (
    <section
      aria-label={t`Live conversation`}
      className="app-no-drag fixed inset-x-3 top-[calc(env(safe-area-inset-top)+80px)] z-50 mx-auto max-w-md rounded-[28px] border border-border bg-card/95 p-2 shadow-lg backdrop-blur-xl md:inset-x-auto md:end-4 md:top-24 md:w-96"
    >
      <div className="flex min-w-0 items-center gap-2">
        <span
          aria-hidden="true"
          className="grid size-10 shrink-0 place-items-center rounded-full bg-muted text-foreground"
        >
          <AudioLines size={20} strokeWidth={1.6} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">
            {botName || t`Live conversation`}
          </p>
          <p role="status" className="truncate text-xs text-muted-foreground">
            {status}
          </p>
        </div>
        {!failed && !connecting && !reconnecting ? (
          <div aria-hidden="true" className="flex h-8 shrink-0 items-center gap-0.5 px-1">
            {WAVE_HEIGHTS.map((height, index) => (
              <span
                key={index}
                className={cn(
                  "h-7 w-0.5 rounded-full bg-foreground/70 transition-transform duration-100 motion-reduce:transition-none",
                  (failed || ended) && "opacity-30",
                  snapshot.outputText && "motion-safe:animate-pulse",
                )}
                style={{
                  transform: `scaleY(${(4 + height * Math.min(1, Math.max(0, level)) * 24) / 28})`,
                }}
              />
            ))}
          </div>
        ) : null}
        {(failed || reconnecting) && onRetry ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label={t`Try again`}
            onClick={onRetry}
            className="size-11 shrink-0 rounded-full"
          >
            <RotateCcw size={18} />
          </Button>
        ) : null}
        {!failed && !ended ? (
          <Button
            variant={snapshot.muted ? "default" : "secondary"}
            size="icon"
            aria-label={snapshot.muted ? t`Unmute` : t`Mute`}
            aria-pressed={snapshot.muted}
            disabled={connecting || reconnecting || historyStatus === "error"}
            onClick={onToggleMute}
            className="size-11 shrink-0 rounded-full"
          >
            <span aria-hidden="true" className="relative size-[18px]">
              <Mic
                size={18}
                className={cn(
                  "absolute inset-0 size-[18px] transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none",
                  snapshot.muted
                    ? "scale-[0.25] opacity-0 blur-[4px]"
                    : "scale-100 opacity-100 blur-none",
                )}
              />
              <MicOff
                size={18}
                className={cn(
                  "absolute inset-0 size-[18px] transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none",
                  snapshot.muted
                    ? "scale-100 opacity-100 blur-none"
                    : "scale-[0.25] opacity-0 blur-[4px]",
                )}
              />
            </span>
          </Button>
        ) : null}
        <Button
          variant={failed || ended ? "ghost" : "destructive"}
          size="icon"
          aria-label={failed || ended ? t`Back to text` : t`End conversation`}
          onClick={failed || ended ? onBackToText : onEnd}
          className="size-11 shrink-0 rounded-full"
        >
          {failed || ended ? <XIcon size={18} /> : <PhoneOff size={18} />}
        </Button>
      </div>
      {failed && snapshot.error ? (
        <p role="alert" className="text-pretty px-3 pb-2 pt-2 text-xs text-destructive">
          {error}
        </p>
      ) : null}
      {historyStatus === "error" ? (
        <div role="alert" className="flex items-center gap-2 px-3 pb-2 pt-2 text-xs text-warning">
          <span className="flex-1 text-pretty">{t`Voice history was not saved. Retry saving before continuing.`}</span>
          <Button
            variant="ghost"
            className="min-h-11 shrink-0"
            onClick={onRetryHistory}
          >{t`Retry saving`}</Button>
        </div>
      ) : null}
    </section>
  );
}
